import { FC, useCallback, useEffect, useState } from "react";
import { CDialog } from "../Dialogs/CDialog";
import { performEnterpriseGPTApiCall } from "../../Hooks/useMiddlewareApiCall";
import { ISurveyAnswer, SurveyResponse } from "../../EnterpriseGPTApi/EnterpriseGPTApiV1Client";
import { useNotification } from "../../Contexts/NotificationContext";
import { useConversations, useSurvey } from "../../store/hooks";
import moment from "moment";
import SurveyView from "./SurveyView";
import { resetSurveyAction } from "../../store/actions/surveyAction";

const SurveyHandler: FC = () => {
    const { chats } = useConversations();
    const { dispatchNotification } = useNotification();
    const [showSurvey, setShowSurvey] = useState<boolean>(false);
    const [isSubmitEnabled, setIsSubmitEnabled] = useState<boolean>(false);
    const { survey, dispatch } = useSurvey();   // get the latest survey from the store, if available
    const [answersDict, setAnswersDict] = useState<{ [key: string]: ISurveyAnswer }>({});

    // Show the survey if there is at least one non-draft chat that started before today.
    useEffect(() => {
        if (survey?.id) {
            setShowSurvey(chats.some(c => c.draft === false && moment(c.startedOn).isBefore(moment().startOf('day'))));
        }
    }, [survey, chats]);

    // Enable the submit button if all mandatory questions have been answered.
    useEffect(() => {
        const isValid = survey?.questions?.every(q => !q.isMandatory || (q.id! in answersDict && !!answersDict[q.id!].value)) ?? false;
        setIsSubmitEnabled(isValid);
    }, [survey, answersDict]);

    const onAnswerChange = (answer: ISurveyAnswer) => {
        setAnswersDict(a => ({
            ...a,
            [answer.id!]: answer
        }));
    };

    const onSubmitSurvey = useCallback(() => {
        performEnterpriseGPTApiCall(async (client, cancelToken) => {
            try {
                const surveyResponse: SurveyResponse = new SurveyResponse({
                    surveyId: survey?.id,
                    answers: Object.values(answersDict).sort((a, b) => parseInt(a.id!) - parseInt(b.id!))
                });

                await client.submitSurveyResponse(surveyResponse, cancelToken);

                setShowSurvey(false);
                dispatch(resetSurveyAction());
            } catch (e) {
                dispatchNotification('error.api.post.conversation', 'error');
            }
        });
    }, [survey, answersDict]);

    // display the survey dialog
    return <CDialog open={showSurvey} onPrimaryAction={onSubmitSurvey} primaryActionText="Submit" disablePrimaryAction={!isSubmitEnabled}>
        <SurveyView surveyModel={survey} onAnswerChange={onAnswerChange} />
    </CDialog>
};

export default SurveyHandler;